import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getCompanyProfile, updateCompanyProfile } from '@mkcore/platform/schema';
import { TimeSettingsCard } from '@/features/time/settings-card';
import { NumberingCard } from '@/features/billing/numbering-card';
import { queryOptions } from '@tanstack/react-query';
import { Button, Card, ErrorState, Field, Input, PageHeader, Skeleton, Textarea } from '@mkcore/ui';
import { ApiError, call } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/company')({ component: Company });

export const companyQuery = queryOptions({ queryKey: ['admin', 'company'], queryFn: () => call(getCompanyProfile, {}) });

const FIELDS = ['legalName', 'tradeName', 'street', 'postalCode', 'city', 'country', 'email', 'phone', 'website', 'registrationNumber', 'vatNumber', 'iban', 'bic', 'currency', 'timezone', 'invoiceNumberFormat', 'creditNoteNumberFormat', 'paymentTermDays', 'fiscalYearStartMonth', 'invoiceFooter', 'invoiceNotes'] as const;
type Form = Record<(typeof FIELDS)[number], string>;

/** The company profile (CO-1301): the legal identity for invoices and the numbering format (CO-0337, CO-0366). */
function Company() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('admin:update');
  const q = useQuery(companyQuery);
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setForm(null), [q.data?.version]);
  const save = useActionMutation(updateCompanyProfile, { invalidate: [['admin', 'company']], success: t('common.saved'), silentErrors: true });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const c = q.data;
  const f: Form = form ?? (Object.fromEntries(FIELDS.map((k) => [k, c[k] === null ? '' : String(c[k])])) as Form);
  const set = (k: keyof Form, v: string) => setForm({ ...f, [k]: v });
  const text = (k: keyof Form, label: string, hint?: string, type = 'text') => (
    <Field label={label} hint={hint} error={errors[k]}>
      {(p) => <Input {...p} type={type} value={f[k]} disabled={!canUpdate} onChange={(e) => set(k, e.target.value)} />}
    </Field>
  );
  const submit = () =>
    save.mutate(
      {
        input: {
          ...Object.fromEntries(FIELDS.filter((k) => !['paymentTermDays', 'fiscalYearStartMonth', 'creditNoteNumberFormat'].includes(k)).map((k) => [k, f[k]])),
          paymentTermDays: Number(f.paymentTermDays),
          fiscalYearStartMonth: Number(f.fiscalYearStartMonth),
          creditNoteNumberFormat: f.creditNoteNumberFormat || null,
          expectedVersion: c.version || undefined,
        },
      },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) }), onSuccess: () => setErrors({}) },
    );
  return (
    <>
      <PageHeader title={t('company.title')} description={t('company.description')} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-lg font-bold">{t('company.identity')}</h2>
          {text('legalName', t('company.fields.legalName'))}
          {text('tradeName', t('company.fields.tradeName'), t('company.fields.tradeNameHint'))}
          {text('street', t('org.fields.street'))}
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
            {text('postalCode', t('org.fields.postalCode'))}
            {text('city', t('org.fields.city'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('country', t('org.fields.country'))}
            {text('currency', t('org.fields.currency'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('email', t('common.email'), undefined, 'email')}
            {text('phone', t('company.fields.phone'))}
          </div>
          {text('website', t('org.fields.website'))}
        </Card>
        <div className="grid content-start gap-6">
          <Card className="grid gap-3 p-4">
            <h2 className="text-lg font-bold">{t('company.legal')}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {text('registrationNumber', t('org.fields.registrationNumber'))}
              {text('vatNumber', t('org.fields.vatNumber'))}
            </div>
            <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
              {text('iban', 'IBAN')}
              {text('bic', 'BIC')}
            </div>
          </Card>
          <Card className="grid gap-3 p-4">
            <h2 className="text-lg font-bold">{t('company.invoicing')}</h2>
            {text('invoiceNumberFormat', t('company.fields.invoiceNumberFormat'), t('company.fields.numberFormatHint'))}
            {text('creditNoteNumberFormat', t('company.fields.creditNoteNumberFormat'), t('company.fields.creditNoteFormatHint'))}
            <div className="grid gap-3 sm:grid-cols-2">
              {text('paymentTermDays', t('company.fields.paymentTermDays'), undefined, 'number')}
              {text('fiscalYearStartMonth', t('company.fields.fiscalYearStartMonth'), undefined, 'number')}
            </div>
            {text('timezone', t('settings.timezone'))}
            <Field label={t('company.fields.invoiceNotes')} hint={t('company.fields.invoiceNotesHint')}>
              {(p) => <Textarea {...p} rows={2} value={f.invoiceNotes} disabled={!canUpdate} onChange={(e) => set('invoiceNotes', e.target.value)} />}
            </Field>
            <Field label={t('company.fields.invoiceFooter')} hint={t('company.fields.invoiceFooterHint')}>
              {(p) => <Textarea {...p} rows={2} value={f.invoiceFooter} disabled={!canUpdate} onChange={(e) => set('invoiceFooter', e.target.value)} />}
            </Field>
          </Card>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          {canUpdate && (
            <div className="flex justify-end">
              <Button disabled={!form} loading={save.isPending} onClick={submit}>
                {t('common.save')}
              </Button>
            </div>
          )}
          <NumberingCard />
          <RetentionCard />
          <TimeSettingsCard />
        </div>
      </div>
    </>
  );
}

const RETENTION_KINDS = ['inboundMail', 'agentTrace', 'jobData'] as const;

/** How long content is kept before the nightly cleanup erases it (CO-0030); the metadata stays. */
function RetentionCard() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('admin:update');
  const q = useQuery(companyQuery);
  const [days, setDays] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string>();
  useEffect(() => setDays(null), [q.data?.version]);
  const save = useActionMutation(updateCompanyProfile, { invalidate: [['admin', 'company']], success: t('common.saved'), silentErrors: true });
  if (!q.data) return null;
  const c = q.data;
  const d = days ?? Object.fromEntries(RETENTION_KINDS.map((k) => [k, String(c.retention[k])]));
  return (
    <Card className="grid gap-3 p-4" role="region" aria-label={t('company.retention.title')}>
      <h2 className="text-lg font-bold">{t('company.retention.title')}</h2>
      <p className="text-sm text-fg-2">{t('company.retention.description')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {RETENTION_KINDS.map((k) => (
          <Field key={k} label={t(`company.retention.${k}`)}>
            {(p) => <Input {...p} type="number" min={1} value={d[k]} disabled={!canUpdate} onChange={(e) => setDays({ ...d, [k]: e.target.value })} />}
          </Field>
        ))}
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {canUpdate && (
        <div className="flex justify-end">
          <Button
            disabled={!days}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { input: { retention: Object.fromEntries(RETENTION_KINDS.map((k) => [k, Number(d[k])])), expectedVersion: c.version || undefined } },
                { onError: (e) => setError(message(e)), onSuccess: () => setError(undefined) },
              )
            }
          >
            {t('company.retention.save')}
          </Button>
        </div>
      )}
    </Card>
  );
}
